<template>
  <div class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden">
    <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between">
        <div>
          <div class="flex items-center gap-2">
            <ChartBarIcon class="h-5 w-5 text-teal-500" />
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">Capaciteit</h3>
            <button
              @click="showInfo = !showInfo"
              class="text-gray-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors"
              title="Uitleg over de capaciteitsgrafiek"
            >
              <InformationCircleIcon class="h-5 w-5" />
            </button>
          </div>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{ scopeLabel }}
          </p>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <Listbox
            v-slot="{ open: teamOpen }"
            :model-value="selectedTeamIds"
            @update:model-value="updateSelectedTeams"
            multiple
          >
            <div class="relative w-[220px]">
              <ListboxButton
                class="relative w-full cursor-pointer rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 pr-8 text-left text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                :class="
                  teamOpen
                    ? 'rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                    : 'rounded-lg'
                "
                title="Capaciteit per team bekijken"
              >
                <span class="block truncate">{{ selectedTeamDisplayText }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    class="h-5 w-5 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute z-50 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-b-lg border border-t-0 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 text-sm shadow-lg focus:outline-none max-h-96 overflow-x-auto overflow-y-auto"
                >
                  <ListboxOption
                    v-for="team in teams"
                    :key="team.team_id"
                    :value="team.team_id"
                    v-slot="{ active, selected }"
                    as="template"
                  >
                    <li
                      :class="[
                        active
                          ? 'cursor-pointer bg-teal-50 dark:bg-teal-900/50 text-teal-900 dark:text-teal-200'
                          : 'cursor-pointer text-gray-700 dark:text-gray-200',
                        'flex items-center gap-2 select-none px-3 py-2'
                      ]"
                    >
                      <CheckIcon
                        v-if="selected"
                        class="h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400"
                        aria-hidden="true"
                      />
                      <span class="block whitespace-nowrap" :class="selected ? 'font-medium' : ''">
                        {{ team.team_name }} ({{ getTeamEmployeeCount(team) }})
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
          <Listbox
            v-if="perFunctionData"
            v-slot="{ open: functionOpen }"
            :model-value="selectedFunctionNames"
            @update:model-value="updateSelectedFunctions"
            multiple
          >
            <div class="relative w-[220px]">
              <ListboxButton
                class="relative w-full cursor-pointer rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 pr-8 text-left text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                :class="
                  functionOpen
                    ? 'rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                    : 'rounded-lg'
                "
                title="Capaciteit per functie bekijken"
              >
                <span class="block truncate">{{ selectedFunctionDisplayText }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    class="h-5 w-5 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <transition
                leave-active-class="transition duration-100 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <ListboxOptions
                  class="absolute z-50 w-max min-w-full max-w-[calc(100vw-2rem)] rounded-b-lg border border-t-0 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 text-sm shadow-lg focus:outline-none max-h-96 overflow-x-auto overflow-y-auto"
                >
                  <ListboxOption
                    v-for="functionGroup in functions"
                    :key="functionGroup.function_name"
                    :value="functionGroup.function_name"
                    v-slot="{ active, selected }"
                    as="template"
                  >
                    <li
                      :class="[
                        active
                          ? 'cursor-pointer bg-teal-50 dark:bg-teal-900/50 text-teal-900 dark:text-teal-200'
                          : 'cursor-pointer text-gray-700 dark:text-gray-200',
                        'flex items-center gap-2 select-none px-3 py-2'
                      ]"
                    >
                      <CheckIcon
                        v-if="selected"
                        class="h-4 w-4 flex-shrink-0 text-teal-600 dark:text-teal-400"
                        aria-hidden="true"
                      />
                      <span class="block whitespace-nowrap" :class="selected ? 'font-medium' : ''">
                        {{ functionGroup.function_name }} ({{
                          getFunctionEmployeeCount(functionGroup)
                        }})
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
          <div
            class="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden"
          >
            <button
              @click="viewMode = 'percentage'"
              :class="[
                'px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'percentage'
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              ]"
            >
              %
            </button>
            <button
              @click="viewMode = 'euro'"
              :class="[
                'px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'euro'
                  ? 'bg-teal-500 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              ]"
            >
              EUR
            </button>
          </div>
        </div>
      </div>
    </div>

    <InfoBox v-if="showInfo" title="Formules & definities" color="blue">
      <div class="space-y-3 text-sm">
        <div>
          <p class="font-semibold mb-1">Formules</p>
          <ul class="list-disc list-inside space-y-1">
            <li><strong>Bruto Declarabiliteit</strong> = Projecturen / Contracturen</li>
            <li>
              <strong>Benutting Netto tijd</strong> = Projectmanagementuren / Beschikbare uren van
              sourcebare collega&rsquo;s
            </li>
            <li>
              <strong>Benutting tijd incl Sourcing</strong> = (Projectmanagementuren + Sourcinguren
              &times; slagingspercentage) / Beschikbare uren van sourcebare collega&rsquo;s. Het
              slagingspercentage is de kans op succes van de kans in Simplicate. Een rol van 2 dpw
              met 50% slagingskans telt dus voor 1 dpw mee.
            </li>
            <li>
              <strong>Sourcinguren</strong> = De geplande uren van medewerkers die in Concretiseren
              definitief aan een kans zijn gekoppeld (actieve toewijzingen). Dagen per week (dpw)
              &times; 8 uur per week, naar rato van de overlap met de maand.
            </li>
            <li>
              <strong>Netto Omzetcapaciteit</strong> = Beschikbare uren van sourcebare
              collega&rsquo;s &times; Standaardtarief.
            </li>
            <li>
              <strong>Gerealiseerd</strong> = Daadwerkelijk geschreven urenomzet uit Simplicate van
              alle sourcebare collega&rsquo;s
            </li>
            <li>
              <strong>Begrote Urenomzet</strong> = De totale urenomzet uit de begroting van alle
              collega&rsquo;s, dus ook van webdevelopers en directie.
            </li>
          </ul>
        </div>
        <div>
          <p class="font-semibold mb-1">Definities</p>
          <ul class="list-disc list-inside space-y-1">
            <li>
              <strong>Sourcebare collega&rsquo;s</strong> = Alle Professionals, Blikondernemers,
              Business developers en Directors. Dus alle Frisse Blikkers, excl. Enable, Directie,
              Stagiaires, Webdevelopers en Samenwerkingspartners
            </li>
            <li>
              <strong>Contracturen</strong> = Je contracturen op basis van je arbeidsovereenkomst
              ofwel 40 uur per week als je fulltime werkt. Per periode wordt rekening gehouden met
              vaste vrije dagen zoals kerst.
            </li>
            <li>
              <strong>Beschikbare uren</strong> = Contracturen -/- (Verlof, Verzuim en Interne uren
              zoals PL-uren en Drempelverlagende uren). Dit aantal uren wordt vermenigvuldigd met
              90% voor alle professionals of 50% voor alle Blikondernemers en Business Developers.
            </li>
            <li>
              <strong>Verlof</strong> = Alle verlofuren bestaande uit Vakantiedagen +
              Feestdagenverlof + Tijd voor tijd + Buitengewoon verlof + Onbetaald verlof +
              Ouderschapsverlof + Zwangerschapsverlof + Partnerverlof + Zorgverlof (kortdurend)
            </li>
            <li><strong>Verzuim</strong> = Alle verzuimuren</li>
            <li>
              <strong>Interne uren</strong> = Alle interne uren zoals Performance en
              Expertiseleadtijd + Interne drempelverlagende uren + Interne uren.
            </li>
            <li>
              <strong>Standaardtarief</strong> = Het standaard verkooptarief uit jouw rooster in
              Simplicate. Deze is afhankelijk van jouw functie.
            </li>
          </ul>
        </div>
        <div>
          <p class="font-semibold mb-1">Good to know</p>
          <ul class="list-disc list-inside space-y-1">
            <li>
              De FTE factor van een collega die vandaag van toepassing is, geldt voor de gehele
              periode uit de grafiek. Iemand die van FTE is gewijzigd, staat in het verleden dus
              voor een verkeerde FTE erin.
            </li>
            <li>
              Hetzelfde geldt voor de sourcing-status van toewijzingen: een toewijzing die nu actief
              of juist released is, telt zo voor alle maanden in de grafiek.
            </li>
            <li>
              Dit geldt niet voor indienst, uitdienst en standaardtarief. Deze worden wel correct
              verwerkt per periode.
            </li>
          </ul>
        </div>
      </div>
    </InfoBox>

    <div class="p-5">
      <VChart :key="viewMode" :option="chartOption" autoresize style="width: 100%; height: 350px" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import {
  ChartBarIcon,
  InformationCircleIcon,
  ChevronUpDownIcon,
  CheckIcon
} from '@heroicons/vue/24/outline'
import InfoBox from './InfoBox.vue'

use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const props = defineProps({
  capacityData: { type: Object, required: true },
  perTeamData: { type: Object, default: null },
  perFunctionData: { type: Object, default: null },
  perTeamFunctionData: { type: Object, default: null },
  budgetData: { type: Array, default: () => [] },
  months: { type: Array, default: null }
})

const viewMode = ref('percentage')
const showInfo = ref(false)
const selectedTeamIds = ref([])
const selectedFunctionNames = ref([])

// Drilldown dimensions from the backend. Each selector is independent; the
// team-function groups provide the intersection when both are selected.
const teams = computed(() => props.perTeamData?.teams || [])
const functions = computed(() =>
  (props.perFunctionData?.functions || []).filter(
    functionGroup => functionGroup.function_name && functionGroup.function_name !== 'Geen functie'
  )
)
const teamFunctionGroups = computed(() =>
  (props.perTeamFunctionData?.groups || []).filter(
    group => group.function_name && group.function_name !== 'Geen functie'
  )
)

const employeeCount = item => Number(item?.employee_count) || 0

const getTeamEmployeeCount = team => {
  if (selectedFunctionNames.value.length === 0 || !props.perTeamFunctionData) {
    return employeeCount(team)
  }

  return teamFunctionGroups.value
    .filter(
      group =>
        group.team_id === team.team_id && selectedFunctionNames.value.includes(group.function_name)
    )
    .reduce((total, group) => total + employeeCount(group), 0)
}

const getFunctionEmployeeCount = functionGroup => {
  if (selectedTeamIds.value.length === 0 || !props.perTeamFunctionData) {
    return employeeCount(functionGroup)
  }

  return teamFunctionGroups.value
    .filter(
      group =>
        group.function_name === functionGroup.function_name &&
        selectedTeamIds.value.includes(group.team_id)
    )
    .reduce((total, group) => total + employeeCount(group), 0)
}

const updateSelectedTeams = nextValue => {
  selectedTeamIds.value = Array.isArray(nextValue) ? nextValue : []
}

const updateSelectedFunctions = nextValue => {
  selectedFunctionNames.value = Array.isArray(nextValue) ? nextValue : []
}

const selectedTeamNames = computed(() =>
  selectedTeamIds.value
    .map(teamId => teams.value.find(team => team.team_id === teamId)?.team_name)
    .filter(Boolean)
)

const selectedTeamDisplayText = computed(() => {
  if (selectedTeamIds.value.length === 0) return 'Hele bedrijf'
  if (selectedTeamIds.value.length === 1) return selectedTeamNames.value[0] || '1 geselecteerd'
  return `${selectedTeamIds.value.length} geselecteerd`
})

const selectedFunctionDisplayText = computed(() => {
  if (selectedFunctionNames.value.length === 0) return 'Alle functies'
  if (selectedFunctionNames.value.length === 1) return selectedFunctionNames.value[0]
  return `${selectedFunctionNames.value.length} geselecteerd`
})

const selectedMetricGroups = computed(() => {
  if (selectedTeamIds.value.length > 0 && selectedFunctionNames.value.length > 0) {
    return teamFunctionGroups.value.filter(
      group =>
        selectedTeamIds.value.includes(group.team_id) &&
        selectedFunctionNames.value.includes(group.function_name)
    )
  }
  if (selectedFunctionNames.value.length > 0) {
    return functions.value.filter(functionGroup =>
      selectedFunctionNames.value.includes(functionGroup.function_name)
    )
  }
  if (selectedTeamIds.value.length > 0) {
    return teams.value.filter(team => selectedTeamIds.value.includes(team.team_id))
  }
  return []
})

// Metrics shown in the chart. Empty selections show company-wide data. When
// multiple groups are selected, additive values are summed and percentages
// are recalculated from those totals rather than averaged.
const currentMetrics = computed(() => {
  if (selectedTeamIds.value.length === 0 && selectedFunctionNames.value.length === 0) {
    return props.capacityData.metrics
  }

  const groups = selectedMetricGroups.value
  if (groups.length === 1) return groups[0].metrics || {}
  return aggregateMetrics(groups.map(group => group.metrics || {}))
})

const scopeLabel = computed(() => {
  const teamSelected = selectedTeamIds.value.length > 0
  const functionSelected = selectedFunctionNames.value.length > 0
  const teamLabel =
    selectedTeamIds.value.length === 1
      ? selectedTeamNames.value[0] || 'team'
      : `${selectedTeamIds.value.length} teams`
  const functionLabel =
    selectedFunctionNames.value.length === 1
      ? selectedFunctionNames.value[0]
      : `${selectedFunctionNames.value.length} functies`

  if (teamSelected && functionSelected) {
    return `Netto Omzetcapaciteit & Benutting Netto Tijd — ${functionLabel} binnen ${teamLabel}`
  }
  if (functionSelected) {
    return `Netto Omzetcapaciteit & Benutting Netto Tijd — ${functionLabel}`
  }
  if (teamSelected) {
    return `Netto Omzetcapaciteit & Benutting Netto Tijd — ${teamLabel}`
  }
  return 'Netto Omzetcapaciteit & Benutting Netto Tijd op basis van rooster-tarief'
})

const isDarkMode = ref(false)

const updateDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

let observer = null

onMounted(() => {
  updateDarkMode()
  observer = new MutationObserver(updateDarkMode)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

function formatMonthLabel(monthKey) {
  const [year, month] = monthKey.split('-')
  const monthNames = [
    'jan',
    'feb',
    'mrt',
    'apr',
    'mei',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec'
  ]
  return `${monthNames[parseInt(month) - 1]} '${year.slice(2)}`
}

function formatCurrency(amount) {
  if (!amount) return '€ 0'
  return `€ ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
}

function formatPct(pct) {
  if (pct == null) return '0%'
  return `${pct.toFixed(1)}%`
}

const ADDITIVE_METRIC_FIELDS = [
  'contracttijd',
  'bruto_capaciteit',
  'netto_capaciteit',
  'pm_hours',
  'sourcing_hours',
  'sourcing_hours_gewogen',
  'omzetcapaciteit',
  'gerealiseerd'
]

const roundMetric = value => Math.round((value + Number.EPSILON * 100) * 100) / 100

function aggregateMetrics(metricGroups) {
  const months = props.months || props.capacityData.months || []
  const aggregated = {}

  for (const month of months) {
    const totals = Object.fromEntries(ADDITIVE_METRIC_FIELDS.map(field => [field, 0]))

    for (const metrics of metricGroups) {
      const monthMetrics = metrics[month]
      if (!monthMetrics) continue
      for (const field of ADDITIVE_METRIC_FIELDS) {
        totals[field] += Number(monthMetrics[field]) || 0
      }
    }

    const contracttijd = totals.contracttijd
    const netto = totals.netto_capaciteit
    const pmHours = totals.pm_hours
    const sourcingHours = totals.sourcing_hours
    const sourcingHoursGewogen = totals.sourcing_hours_gewogen
    const omzetcap = totals.omzetcapaciteit
    const gerealiseerd = totals.gerealiseerd

    aggregated[month] = {
      contracttijd: roundMetric(contracttijd),
      bruto_capaciteit: roundMetric(totals.bruto_capaciteit),
      netto_capaciteit: roundMetric(netto),
      pm_hours: roundMetric(pmHours),
      sourcing_hours: roundMetric(sourcingHours),
      sourcing_hours_gewogen: roundMetric(sourcingHoursGewogen),
      benutting_contracttijd: contracttijd > 0 ? roundMetric((pmHours / contracttijd) * 100) : 0,
      tijdsbenutting: netto > 0 ? roundMetric((pmHours / netto) * 100) : 0,
      tijdsbenutting_incl_sourcing:
        netto > 0 ? roundMetric(((pmHours + sourcingHours) / netto) * 100) : 0,
      tijdsbenutting_incl_sourcing_gewogen:
        netto > 0 ? roundMetric(((pmHours + sourcingHoursGewogen) / netto) * 100) : 0,
      omzetcapaciteit: roundMetric(omzetcap),
      gerealiseerd: roundMetric(gerealiseerd),
      omzetbenutting: omzetcap > 0 ? roundMetric((gerealiseerd / omzetcap) * 100) : 0,
      budget_hours_revenue: undefined
    }
  }

  return aggregated
}

// Legend tooltip descriptions per series name (plain Dutch, no abbreviations)
const legendDescriptions = {
  'Bruto Declarabiliteit':
    'Hoeveel procent van de totale contracturen besteed wordt aan projecten.',
  'Benutting Netto Tijd':
    'Hoeveel procent van de beschikbare uren besteed wordt aan projecten. De beschikbare uren zijn de contracturen minus verlof, verzuim en interne uren, en daar 90% of 50% van afhankelijk van de functie.',
  'Benutting tijd incl Sourcing':
    'Hoeveel procent van de beschikbare uren besteed wordt aan projecten en sourcing, waarbij sourcinguren zijn vermenigvuldigd met het slagingspercentage van de kans zoals die in Simplicate staat. Een rol van 2 dpw met 50% slagingskans telt dus voor 1 dpw mee.',
  Omzetbenutting: 'Hoeveel procent van de mogelijke omzet daadwerkelijk is gerealiseerd.',
  'Netto Omzetcapaciteit':
    'De omzet die behaald kan worden als alle beschikbare uren worden ingezet op project. De netto tijd maal het standaardtarief dus.',
  Omzetrealisatie: 'De daadwerkelijk geschreven urenomzet.',
  'Begrote Urenomzet':
    'De begrootte urenomzet van iedereen, dus inclusief directie en webdevelopers.'
}

// Budget lookup by month key
const budgetByMonth = computed(() => {
  const map = {}
  for (const b of props.budgetData) {
    const key = `${b.year}-${String(b.month).padStart(2, '0')}`
    map[key] = b
  }
  return map
})

const chartOption = computed(() => {
  const dark = isDarkMode.value
  const colors = {
    textPrimary: dark ? '#e5e7eb' : '#374151',
    textSecondary: dark ? '#9ca3af' : '#6b7280',
    border: dark ? '#4b5563' : '#e5e7eb',
    tooltipBg: dark ? '#1f2937' : '#ffffff',
    tooltipBorder: dark ? '#374151' : '#e5e7eb'
  }

  const months = props.months || props.capacityData.months
  const metrics = currentMetrics.value
  const monthLabels = months.map(formatMonthLabel)

  if (viewMode.value === 'euro') {
    return buildEuroOption(months, metrics, monthLabels, colors)
  }
  return buildPercentageOption(months, metrics, monthLabels, colors)
})

function buildPercentageOption(months, metrics, monthLabels, colors) {
  const brutoDeclarabiliteit = months.map(m => metrics[m]?.benutting_contracttijd || 0)
  const tijdsbenutting = months.map(m => metrics[m]?.tijdsbenutting || 0)
  const tijdsbenuttingInclSourcing = months.map(
    m => metrics[m]?.tijdsbenutting_incl_sourcing_gewogen || 0
  )
  const omzetbenutting = months.map(m => metrics[m]?.omzetbenutting || 0)

  // Benutting tijd incl Sourcing kan boven de 100% uitkomen; schaal de y-as mee
  // (afgerond op 10%) met 100% als minimum plafond.
  const maxData = Math.max(
    ...brutoDeclarabiliteit,
    ...tijdsbenutting,
    ...tijdsbenuttingInclSourcing,
    ...omzetbenutting,
    0
  )
  const yAxisMax = Math.max(100, Math.ceil(maxData / 10) * 10)

  return {
    animation: true,
    grid: { left: 60, right: 30, top: 50, bottom: 40 },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      extraCssText: 'z-index: 9999;',
      formatter: params => {
        if (!params?.length) return ''
        const monthIdx = params[0].dataIndex
        const m = months[monthIdx]
        const met = metrics[m]
        let html = `<div style="font-weight:600;margin-bottom:6px;">${params[0].axisValue}</div>`
        html += `<div style="margin:3px 0;"><span style="display:inline-block;width:10px;height:2px;background:#f59e0b;margin-right:6px;border-top:2px dashed #f59e0b;"></span>Bruto Declarabiliteit: <strong>${formatPct(met?.benutting_contracttijd)}</strong></div>`
        html += `<div style="margin:3px 0;"><span style="display:inline-block;width:10px;height:2px;background:#8b5cf6;margin-right:6px;border-top:2px solid #8b5cf6;"></span>Benutting Netto Tijd: <strong>${formatPct(met?.tijdsbenutting)}</strong></div>`
        html += `<div style="margin:3px 0;"><span style="display:inline-block;width:10px;height:2px;background:#06b6d4;margin-right:6px;border-top:2px solid #06b6d4;"></span>Benutting tijd incl Sourcing: <strong>${formatPct(met?.tijdsbenutting_incl_sourcing_gewogen)}</strong>${met?.sourcing_hours_gewogen ? ` <span style="color:${colors.textSecondary};">(${met.sourcing_hours_gewogen.toFixed(1)}u sourcing)</span>` : ''}</div>`
        html += `<div style="margin:3px 0;"><span style="display:inline-block;width:10px;height:2px;background:#10b981;margin-right:6px;border-top:2px solid #10b981;"></span>Omzetbenutting: <strong>${formatPct(met?.omzetbenutting)}</strong></div>`
        return html
      }
    },
    legend: {
      top: 0,
      type: 'scroll',
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      pageTextStyle: { color: colors.textSecondary },
      pageIconColor: colors.textSecondary,
      pageIconInactiveColor: colors.border,
      tooltip: {
        show: true,
        confine: true,
        position: 'bottom',
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        textStyle: { color: colors.textPrimary, fontSize: 12 },
        extraCssText: 'text-align:left;white-space:pre-line;max-width:280px;',
        formatter: params => legendDescriptions[params.name] || params.name
      }
    },
    xAxis: {
      type: 'category',
      data: monthLabels,
      boundaryGap: false,
      axisLabel: { color: colors.textPrimary, fontSize: 12 },
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { lineStyle: { color: colors.border } }
    },
    yAxis: [
      {
        type: 'value',
        min: 0,
        max: yAxisMax,
        axisLabel: {
          color: colors.textSecondary,
          fontSize: 11,
          formatter: v => `${v}%`
        },
        axisLine: { lineStyle: { color: colors.border } },
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      }
    ],
    series: [
      {
        name: 'Bruto Declarabiliteit',
        type: 'line',
        data: brutoDeclarabiliteit,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2, color: '#f59e0b', type: 'dashed' },
        itemStyle: { color: '#f59e0b' }
      },
      {
        name: 'Benutting Netto Tijd',
        type: 'line',
        data: tijdsbenutting,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2, color: '#8b5cf6' },
        itemStyle: { color: '#8b5cf6' }
      },
      {
        name: 'Benutting tijd incl Sourcing',
        type: 'line',
        data: tijdsbenuttingInclSourcing,
        smooth: true,
        symbol: 'diamond',
        symbolSize: 6,
        lineStyle: { width: 2, color: '#06b6d4', type: 'solid' },
        itemStyle: { color: '#06b6d4' }
      },
      {
        name: 'Omzetbenutting',
        type: 'line',
        data: omzetbenutting,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2, color: '#10b981' },
        itemStyle: { color: '#10b981' }
      }
    ]
  }
}

function buildEuroOption(months, metrics, monthLabels, colors) {
  const showBudget = selectedTeamIds.value.length === 0 && selectedFunctionNames.value.length === 0
  const omzetcapData = months.map(m => metrics[m]?.omzetcapaciteit || 0)
  const gerealiseerd = months.map(m => metrics[m]?.gerealiseerd || 0)
  const budgetLine = months.map(m => {
    const met = metrics[m]
    if (met?.budget_hours_revenue) return met.budget_hours_revenue
    const budget = budgetByMonth.value[m]
    return budget ? parseFloat(budget.budget_hours_revenue) : 0
  })

  return {
    animation: true,
    grid: { left: 80, right: 30, top: 50, bottom: 40 },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      extraCssText: 'z-index: 9999;',
      formatter: params => {
        if (!params?.length) return ''
        const monthIdx = params[0].dataIndex
        const m = months[monthIdx]
        const met = metrics[m]
        let html = `<div style="font-weight:600;margin-bottom:6px;">${params[0].axisValue}</div>`
        html += `<div style="margin:3px 0;"><span style="display:inline-block;width:10px;height:10px;background:${isDarkMode.value ? '#374151' : '#e5e7eb'};border-radius:2px;margin-right:6px;border:1px solid ${colors.border};"></span>Netto Omzetcapaciteit: <strong>${formatCurrency(met?.omzetcapaciteit)}</strong></div>`
        html += `<div style="margin:3px 0;"><span style="display:inline-block;width:10px;height:10px;background:#3b82f6;border-radius:2px;margin-right:6px;"></span>Omzetrealisatie: <strong>${formatCurrency(met?.gerealiseerd)}</strong></div>`
        const budgetVal = budgetLine[monthIdx]
        if (showBudget && budgetVal > 0) {
          html += `<div style="margin:3px 0;"><span style="display:inline-block;width:10px;height:2px;background:#f59e0b;margin-right:6px;border-top:2px dashed #f59e0b;"></span>Begrote Urenomzet: <strong>${formatCurrency(budgetVal)}</strong></div>`
        }
        if (met?.omzetcapaciteit > 0) {
          html += `<hr style="border-color:${colors.border};margin:6px 0;" />`
          html += `<div style="margin:3px 0;">Omzetbenutting: <strong>${formatPct(met?.omzetbenutting)}</strong></div>`
        }
        return html
      }
    },
    legend: {
      top: 0,
      type: 'scroll',
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      pageTextStyle: { color: colors.textSecondary },
      pageIconColor: colors.textSecondary,
      pageIconInactiveColor: colors.border,
      tooltip: {
        show: true,
        confine: true,
        position: 'bottom',
        backgroundColor: colors.tooltipBg,
        borderColor: colors.tooltipBorder,
        textStyle: { color: colors.textPrimary, fontSize: 12 },
        extraCssText: 'text-align:left;white-space:pre-line;max-width:280px;',
        formatter: params => legendDescriptions[params.name] || params.name
      }
    },
    xAxis: {
      type: 'category',
      data: monthLabels,
      axisLabel: { color: colors.textPrimary, fontSize: 12 },
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { lineStyle: { color: colors.border } }
    },
    yAxis: [
      {
        type: 'value',
        name: 'Omzet',
        nameTextStyle: { fontSize: 11, color: colors.textSecondary },
        axisLabel: {
          color: colors.textSecondary,
          fontSize: 11,
          formatter: v => {
            if (v >= 1000000) return `€${(v / 1000000).toFixed(1)}M`
            if (v >= 1000) return `€${(v / 1000).toFixed(0)}K`
            return `€${v}`
          }
        },
        axisLine: { lineStyle: { color: colors.border } },
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      }
    ],
    series: [
      {
        name: 'Netto Omzetcapaciteit',
        type: 'bar',
        data: omzetcapData,
        barMaxWidth: 40,
        itemStyle: {
          color: isDarkMode.value ? '#374151' : '#e5e7eb',
          borderRadius: [4, 4, 0, 0]
        },
        z: 1
      },
      {
        name: 'Omzetrealisatie',
        type: 'bar',
        data: gerealiseerd,
        barMaxWidth: 40,
        barGap: '-100%',
        itemStyle: { color: '#3b82f6', borderRadius: [4, 4, 0, 0] },
        z: 2
      },
      ...(showBudget
        ? [
            {
              name: 'Begrote Urenomzet',
              type: 'line',
              data: budgetLine,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: { width: 2, color: '#f59e0b', type: 'dashed' },
              itemStyle: { color: '#f59e0b' }
            }
          ]
        : [])
    ]
  }
}
</script>
